import { fetchCategoryRec } from '@api/categoryRec';
import { createClient } from '@redux/bookings';
import type { CategoryScoreRec } from '@typings/api/categoryRec';
import { useEffect, useState } from 'react';
import { getBookedCategoryCode } from '../../../../../../lib/mcvrm/hooks/useCategories';

const DEFAULT_CATEGORY_RECOMMENDATIONS = 9;

async function getExcludedCategoryCodes(
	excludeCategoryCode: Category.CategoryCode | undefined,
	recommendationMemberId: string,
): Promise<Category.CategoryCode[]> {
	const excludedCategoryCodes = excludeCategoryCode
		? new Set([excludeCategoryCode])
		: new Set<Category.CategoryCode>();

	const weddingClient = createClient(excludeCategoryCode, {
		id: recommendationMemberId,
	});

	let bookings = [];

	try {
		bookings =
			typeof weddingClient.getAllBookings === 'function'
				? await weddingClient.getAllBookings()
				: typeof weddingClient.getBookings === 'function'
					? await weddingClient.getBookings()
					: [];
	} catch {
		bookings = [];
	}

	for (const booking of bookings || []) {
		const bookedCategoryCode = getBookedCategoryCode(booking);
		if (bookedCategoryCode) {
			excludedCategoryCodes.add(bookedCategoryCode);
		}
	}

	// Exclude categories that pvrec cross-category ROS does not support
	excludedCategoryCodes.add('TRA');
	excludedCategoryCodes.add('BAR');
	excludedCategoryCodes.add('OPC');
	excludedCategoryCodes.add('FAV');
	excludedCategoryCodes.add('INV');
	excludedCategoryCodes.add('JWL');
	excludedCategoryCodes.add('DNC');
	excludedCategoryCodes.add('DEC');
	excludedCategoryCodes.add('PHB');
	excludedCategoryCodes.add('RNT');
	excludedCategoryCodes.add('HRB');
	excludedCategoryCodes.add('ENS');
	excludedCategoryCodes.add('TRV');

	return Array.from(excludedCategoryCodes);
}

interface UseCategoriesResult {
	categories: CategoryScoreRec[];
	selectedCategoryCode: Category.CategoryCode | null;
	setSelectedCategoryCode: (code: Category.CategoryCode) => void;
	isLoading: boolean;
	error: string | null;
}

export const useCategories = (
	memberId: string | null,
	anonymousId: string,
	excludeCategoryCode?: Category.CategoryCode,
	marketCode?: string | null,
): UseCategoriesResult => {
	const [categories, setCategories] = useState<CategoryScoreRec[]>([]);
	const [selectedCategoryCode, setSelectedCategoryCode] =
		useState<Category.CategoryCode | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;

		const loadCategories = async () => {
			const recommendationMemberId = memberId ?? anonymousId;
			if (!recommendationMemberId) {
				setIsLoading(false);
				return;
			}

			const excludeCategoriesCodes = await getExcludedCategoryCodes(
				excludeCategoryCode,
				recommendationMemberId,
			);

			try {
				setIsLoading(true);
				setError(null);

				const { categories: fetchedCategories } = await fetchCategoryRec(
					recommendationMemberId,
					excludeCategoriesCodes,
					DEFAULT_CATEGORY_RECOMMENDATIONS,
					marketCode || undefined,
				);

				if (cancelled) return;

				if (!fetchedCategories || fetchedCategories.length === 0) {
					setError('No recommended categories found');
					setIsLoading(false);
					return;
				}

				setCategories(fetchedCategories);
				setSelectedCategoryCode(
					fetchedCategories[0].category as Category.CategoryCode,
				);
				setIsLoading(false);
			} catch (err) {
				if (!cancelled) {
					setError('Failed to load categories');
					setIsLoading(false);
				}
			}
		};

		loadCategories();

		return () => {
			cancelled = true;
		};
	}, [memberId, anonymousId, excludeCategoryCode, marketCode]);

	return {
		categories,
		selectedCategoryCode,
		setSelectedCategoryCode,
		isLoading,
		error,
	};
};
